/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable react/jsx-props-no-spreading */
import { FieldPath, FormProvider, useFieldArray } from 'react-hook-form';
import React from 'react';

import { format, validate } from 'rut.js';
import clsx from 'clsx';
import { InputField, RadioField } from '../Forms/Fields';
import { FormProps } from '../../types/fileExplorerTypes';
import { DropzoneField } from '../Forms/Fields/DropzoneField';
import { formsConfig } from '../FileExplorer/Forms/config';
import { ContractSigningForm as ContractSigningFormType } from './schemas';
import { ContractSigningSignerOrderReminder } from './ContractSigningRestrictions';
import { DraggableAppendableField } from '../Forms/Fields/DraggableAppendableField';
import { BaseError } from '../Forms/Errors';
import { ContractSigningKindOptions, ContractSigningKindOptionsMap } from './constants';
import { DEFAULT_NEW_SIGNER_VALUES } from './useUploadDocuments';
import { DocumentsValidationList } from './DocumentsValidationList';
import { useDebounce } from '../../hooks/useDebounce';

const MAX_SIGNERS = 3;
const DEBOUNCE_DELAY_FOR_CLIENT_RUT_VALIDATION = 0.3;
const DEBOUNCE_DELAY_FOR_DOCUMENT_VALIDATION = 0.5;

export function ContractSigningForm(
  // TODO: FormProps should be imported from a common types file
  {
    form, onSubmit, children,
  }: FormProps<ContractSigningFormType>) {
  const {
    fields, append, remove,
  } = useFieldArray<ContractSigningFormType, 'documentsToSign', 'id'>({
    control: form.control,
    name: 'documentsToSign',
  });

  const clientRUT = form.watch('clientRUT');
  const contractSigningKind = form.watch('contractSigningKind');

  // We need to validate the client RUT immediately
  // to get almost inmediate feedback but keep some time
  // to wait for the user to finish typing before enabling/disabling the dropzone
  const slightlyDebouncedClientRUT = useDebounce(
    clientRUT,
    DEBOUNCE_DELAY_FOR_CLIENT_RUT_VALIDATION,
  );
  // We use the debounced client RUT to make validations at document-level
  const debouncedClientRUT = useDebounce(clientRUT, DEBOUNCE_DELAY_FOR_DOCUMENT_VALIDATION);

  const isClientRutValid = validate(slightlyDebouncedClientRUT);

  const handleRemove = (index: number) => {
    remove(index);
  };

  const handleDrop = (acceptedFiles: File[]) => {
    append(acceptedFiles.map((file) => ({
      raw: file,
      id: crypto.randomUUID(),
      document_type_id: 0,
    })));
  };

  const handleRutChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    path: FieldPath<ContractSigningFormType>,
  ) => {
    const rut = event.target.value;
    form.setValue(
      path,
      format(rut),
    );
  };

  const handleContractSigningKindChange = (value: ContractSigningKindOptions) => {
    if (value === ContractSigningKindOptionsMap.JURIDICAL_PERSON) {
      form.setValue('signers', [DEFAULT_NEW_SIGNER_VALUES]);
      form.setValue('clientEmail', undefined);
    }
    if (value === ContractSigningKindOptionsMap.NATURAL_PERSON) {
      form.setValue('signers', []);
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="d-flex flex-column gap-4">

        <RadioField<ContractSigningFormType>
          label="¿Qué tipo de proceso quiere realizar?"
          path="contractSigningKind"
          options={ContractSigningKindOptions.asRadioOptions<ContractSigningFormType>()}
          onChange={handleContractSigningKindChange}
        />

        <InputField<ContractSigningFormType>
          id="clientRUT"
          path="clientRUT"
          label={contractSigningKind === ContractSigningKindOptionsMap.NATURAL_PERSON ? 'RUT del cliente' : 'RUT de la empresa'}
          placeholder="11.111.111-1"
          type="text"
          onChange={(event) => handleRutChange(event, 'clientRUT')}
        />
        {contractSigningKind === ContractSigningKindOptionsMap.NATURAL_PERSON ? (
          <InputField<ContractSigningFormType>
            id="clientEmail"
            path="clientEmail"
            label="Correo del cliente"
            placeholder="correo@email.com"
            type="text"
          />
        )
          : (
            <div>
              <DraggableAppendableField<ContractSigningFormType>
                appendDefaultValues={DEFAULT_NEW_SIGNER_VALUES}
                maxItems={MAX_SIGNERS}
                id="signers"
                name="signers"
                control={form.control}
                rules={{ maxLength: 3 }}
                defaultAppendButtonText="Agregar Rut de apoderado"
                labels={[
                  {
                    title: 'Rut de apoderado',
                    span: 'col-6',
                  },
                  {
                    title: 'Correo',
                    span: 'col-6',
                  },
                ]}
                renderInput={(fieldId, index, isDragging) => (
                  <div className="row w-100 g-2" key={fieldId}>
                    <div className="col-6">
                      <InputField<ContractSigningFormType>
                        id={`rut-${fieldId}`}
                        path={`signers.${index}.rut` as const}
                        placeholder="11.111.111-1"
                        type="text"
                        className={clsx('form-control', { 'bg-info-light border-info': isDragging })}
                        onChange={(event) => handleRutChange(event, `signers.${index}.rut`)}
                      />
                    </div>
                    <div className="col-6">
                      <InputField<ContractSigningFormType>
                        id={`email-${fieldId}`}
                        path={`signers.${index}.email` as const}
                        placeholder="correo@email.com"
                        // We need to use type='text' in order to enable
                        // Zod to make validations, and show the error message.
                        // In other case, the error message is not shown because
                        // the native validation is triggered
                        type="text"
                        className={clsx('form-control', { 'bg-info-light border-info': isDragging })}
                      />
                    </div>
                  </div>
                )}
              />
              <BaseError message={form.formState.errors.signers?.root?.message as string} />
              <ContractSigningSignerOrderReminder />
            </div>
          )}

        <DropzoneField<ContractSigningFormType>
          name="documentsToSign"
          accept={formsConfig.commonAcceptedFiles}
          showPreview
          multiple
          onDrop={handleDrop}
          disabled={!validate(slightlyDebouncedClientRUT)}
          // It's ok to declare the component here because it's a nested component
          // that needs inherited props from the parent component
          // eslint-disable-next-line react/no-unstable-nested-components
          previewComponent={() => (
            <DocumentsValidationList
              files={fields}
              onRemove={handleRemove}
              rut={debouncedClientRUT}
              isRutValid={isClientRutValid}
            />
          )}
        />

        {children}
      </form>
    </FormProvider>
  );
}
